import { requireService } from '@profgeosoft/di-ez';
import { Drawer } from '@repo/go-ui';
import { useGridApiRef, Table } from '@repo/go-table';
import { clsx } from 'clsx';
import { reaction } from 'mobx';
import { useCallback, useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SERVICE_COLUMNS } from 'src/components/directory-table/consts';
import { DirectoryTableMediator } from 'src/components/directory-table/directory-table-mediator';
import { tableSx } from 'src/components/directory-table/table-sx';
import { useGetColumnsGrouping } from 'src/components/directory-table/hooks/use-get-columns-grouping';
import { useTableColumns } from 'src/components/directory-table/hooks/use-table-columns';
import { DisplaySettings } from 'src/components/display-settings/display-settings';
import { wrap } from 'src/packages/mobx-di/wrap';
import { debounce } from 'src/packages/utils/debounce';
import { hasValue } from 'src/packages/utils/has-value';
import { isObjectWithKeys } from 'src/packages/utils/is-object-with-keys';
import { serializeDirectoryRecordToSave } from 'src/services/directory-service/directory-service.utils';
import { TableControl } from 'src/services/directory-service/entities/complex-controls';

import type {
  GridColumnResizeParams,
  GridSortItem,
  GridCellParams,
  GridCellEditStartParams,
  MuiEvent,
  MuiBaseEvent,
  GridCellEditStopParams,
} from '@repo/go-table';
import type { DirectoryRecord } from 'src/services/directory-service/entities/directory-record.entity';
import type { MainDirectoryService } from 'src/services/directory-service/main-directory-service';
import type { OptionsService } from 'src/services/directory-service/options-service';
import type { TTableControlView } from 'src/services/directory-service/types';
import type { TableSettingsService, TTableSortItem } from 'src/services/table-settings-service/table-settings-service';

import styles from './sub-table-component.module.scss';

type Props = {
  parentDirectoryService: MainDirectoryService;
  tableSettingsService: TableSettingsService;
  record: DirectoryRecord;
  view: TTableControlView;
  optionsService: OptionsService;
  initialValue: Record<string | number, unknown>[];
};

const muiLanguageKeys: Record<string, 'ruRU' | 'enUS' | 'esES'> = {
  ru: 'ruRU',
  en: 'enUS',
  es: 'esES',
};

const MIN_VISIBLE_COLUMNS = 3;

export const SubTableComponent = wrap<Props>(function SubTableComponent({
  parentDirectoryService,
  record,
  tableSettingsService,
  view,
  optionsService,
  initialValue,
}) {
  const mainServices = requireService('mainServices');

  const [tableControl] = useState(
    () => new TableControl(mainServices, tableSettingsService, view, optionsService, initialValue),
  );

  const displayManager = tableControl.tableSettingsManager;
  const notifications = requireService('notificationsService');
  const localization = requireService('localizationService');
  const directoryPage = requireService('directoryPage');
  const interTableInteractions = requireService('interTableInteractions');

  const tableApi = useGridApiRef();
  const columns = useTableColumns(tableControl);
  const groupingModel = useGetColumnsGrouping(tableControl.view);

  const { t } = useTranslation('directory');

  const handleWidthChange = ({ colDef: { field }, width }: GridColumnResizeParams) => {
    if (!displayManager) {
      return;
    }

    const columns = tableApi.current.getAllColumns();
    const updatedColumnsWidths: Record<string, number> = {};

    columns.forEach((column) => {
      if (column.field === field) {
        updatedColumnsWidths[field] = width;
      } else if (displayManager.settings.columnsWidth && !displayManager.settings.columnsWidth[column.field]) {
        updatedColumnsWidths[column.field] = column.computedWidth;
      }
    });

    displayManager.onWidthChange(updatedColumnsWidths);
  };

  const handleTableSettingsClick = useCallback(() => {
    displayManager?.setSettingsOpen(true);
    tableApi.current.hideColumnMenu();
  }, [displayManager, tableApi]);

  const slotProps = useMemo(
    () => ({
      columnMenu: {
        onTableSettingsClick: handleTableSettingsClick,
        minVisibleColumns: MIN_VISIBLE_COLUMNS,
      },
    }),
    [handleTableSettingsClick],
  );

  const convertToTableSortItems = (model: GridSortItem[]): TTableSortItem[] => {
    const convertedSortItems: TTableSortItem[] = [];

    model.forEach((item) => {
      if (item.sort === 'asc' || item.sort === 'desc') {
        convertedSortItems.push({
          field: item.field,
          sort: item.sort,
        });
      }
    });

    return convertedSortItems;
  };

  const getCellClassName = useCallback(
    (params: GridCellParams<DirectoryRecord>): string => {
      const record = tableControl.recordsMap.get(params.id);

      if (!record) {
        return '';
      }

      const { fieldRulesData, fieldsErrors } = record;

      const isSelected =
        displayManager?.selectedCellData &&
        displayManager?.selectedCellData.cellField === params.field &&
        displayManager?.selectedCellData.rowId === params.row.recordId;

      return clsx(
        styles.cell,
        fieldsErrors[params.field]?.length && 'go-table__error-cell',
        isSelected && `${styles.selectedCell} Mui-selected`,
        directoryPage.isEditing &&
          (fieldRulesData?.isDisabled || fieldRulesData?.isEnteringBlocked) &&
          styles.blockedCell,
      );
    },
    [tableControl, directoryPage.isEditing, displayManager?.selectedCellData],
  );

  const handleCellDeletePress = (params: GridCellEditStartParams, event: MuiEvent<MuiBaseEvent>) => {
    if (params.reason === 'deleteKeyDown') {
      const cellSelectionModel = tableApi.current.getCellSelectionModel();

      const removeValue = (fieldId: string, recordId: number) => {
        tableControl.directoryViewMediator?.onControlValueChange(recordId, fieldId, null);
      };

      const rowIds = Object.keys(cellSelectionModel);

      if (!rowIds.length) {
        return;
      }

      if (rowIds.length > 1 || Object.keys(cellSelectionModel[rowIds[0]]).length) {
        event.defaultMuiPrevented = true;
        const rows: DirectoryRecord[] = [];

        for (const recordId in cellSelectionModel) {
          if (Number.isNaN(Number(recordId))) {
            console.error('record id is not valid');
            continue;
          }

          const row = tableApi.current.getRow(Number(recordId));

          if (!row) {
            continue;
          }

          rows.push(row);

          for (const fieldId in cellSelectionModel[recordId]) {
            removeValue(fieldId, Number(recordId));
          }
        }

        tableApi.current.updateRows(rows);
      } else {
        removeValue(params.colDef.field, params.row.recordId);
      }
    }
  };

  const handleComboboxEnterPress = (params: GridCellEditStopParams, event: MuiEvent<MuiBaseEvent>) => {
    if (params.reason === 'enterKeyDown') {
      const fieldView = tableControl.controlsViewsMap.get(params.colDef.field);

      if (!fieldView) {
        console.error('cannot find corresponding fieldView');
        return;
      }

      if (fieldView.control === 'MultiComboBox') {
        event.defaultMuiPrevented = true;
      }
    }
  };

  useEffect(() => {
    if (!displayManager || !displayManager.settings?.columnsWidth) {
      return;
    }

    for (const column of tableApi.current.getAllColumns()) {
      if (
        displayManager.settings.columnsWidth[column.field] === column.width ||
        !hasValue(displayManager.settings.columnsWidth[column.field])
      ) {
        continue;
      }

      tableApi.current.setColumnWidth(column.field, displayManager.settings.columnsWidth[column.field]);
    }
  }, [tableApi, columns, displayManager, displayManager?.settings]);

  useEffect(() => {
    if (!displayManager) {
      return;
    }

    if (!displayManager.settings.columnsOrderModel) {
      displayManager.onColumnsOrderModelChange(columns.map((columns) => columns.field));
    }
  }, [columns, displayManager, tableApi]);

  useEffect(() => {
    const mediator = new DirectoryTableMediator(tableControl, tableApi, notifications, localization);

    tableControl.setDirectoryViewMediator(mediator);

    return mediator.init();
  }, [tableControl, localization, notifications, tableApi]);

  useEffect(() => {
    let timer: number | undefined;

    const handlePaste = (event: KeyboardEvent) => {
      if (event.code !== 'KeyV' || (!event.ctrlKey && !event.metaKey)) {
        return;
      }

      if (interTableInteractions.activeTable !== tableControl.entityId) {
        return;
      }

      // игнорируем, если событие реагирует на вставку текста в input поле т.к. в таком случае должна отработать дефолтная логика вставки браузера
      if (event.target instanceof HTMLInputElement) {
        return;
      }

      if (hasValue(timer)) {
        return;
      }

      event.preventDefault();

      tableControl.directoryViewMediator?.pasteData(displayManager?.selectedCellData || undefined);
      timer = window.setTimeout(() => {
        timer = undefined;
      }, 300);
    };

    document.addEventListener('keydown', handlePaste);

    return () => {
      document.removeEventListener('keydown', handlePaste);
      window.clearTimeout(timer);
    };
  }, [tableControl.directoryViewMediator, displayManager, interTableInteractions, tableControl.entityId]);

  useEffect(() => {
    let timer: number | undefined;

    const handleCopy = (event: KeyboardEvent) => {
      if (event.code !== 'KeyC' || (!event.ctrlKey && !event.metaKey)) {
        return;
      }

      if (hasValue(timer)) {
        return;
      }

      const selectedRows = tableApi.current.getSelectedRows();

      if (selectedRows.size < 1) {
        const cellSelection = tableApi.current.getCellSelectionModel();

        const tableCopyTargetData = (() => {
          if (cellSelection && !!Object.keys(cellSelection).length) {
            return cellSelection;
          }

          if (displayManager?.selectedCellData) {
            return {
              [displayManager.selectedCellData.rowId]: {
                [displayManager.selectedCellData.cellField]: true,
              },
            };
          }

          return null;
        })();

        if (!tableCopyTargetData) {
          return;
        }

        if (interTableInteractions.activeTable === tableControl.entityId) {
          tableControl.directoryViewMediator?.copySelectedCellData(tableCopyTargetData);
        }

        timer = window.setTimeout(() => {
          timer = undefined;
        }, 500);

        return;
      }

      tableControl.directoryViewMediator?.copySelectedRowsToClipboard();

      timer = window.setTimeout(() => {
        timer = undefined;
      }, 300);
    };

    document.addEventListener('keydown', handleCopy);

    return () => {
      document.removeEventListener('keydown', handleCopy);
      window.clearTimeout(timer);
    };
  }, [tableControl.directoryViewMediator, displayManager, tableApi, tableControl.entityId, interTableInteractions]);

  useEffect(() => {
    const handleFocusIn = ({ field, row }: GridCellParams) => {
      const focusedCellData = { cellField: field, rowId: row.recordId };
      displayManager?.setSelectedCellData(focusedCellData);
    };

    return tableApi.current.subscribeEvent('cellFocusIn', handleFocusIn);
  }, [displayManager, tableApi]);

  useEffect(() => {
    return reaction(
      () => interTableInteractions.activeTable,
      (tableId) => {
        if (tableControl.entityId === tableId) {
          return;
        }

        tableApi.current.setCellSelectionModel({});
        displayManager.setSelectedCellData(null);
      },
    );
  }, [displayManager, interTableInteractions, tableApi, tableControl.entityId]);

  useEffect(() => {
    return tableApi.current.subscribeEvent('cellMouseDown', () => {
      interTableInteractions.setActiveTable(tableControl.entityId);
    });
  }, [tableControl.entityId, interTableInteractions, tableApi]);

  const syncMainTableWithSubtable = useMemo(() => {
    const _fn = ({ fieldId }: { fieldId: string }) => {
      const fieldView = tableControl.controlsViewsMap.get(fieldId);

      if (!fieldView) {
        console.error('cannot find fieldView');
        return;
      }

      // игнорируем информационные поля
      if (!fieldView.attrName) {
        return;
      }

      const serializedRecords = tableControl.records.map((rec) =>
        serializeDirectoryRecordToSave(rec, tableControl.recordControlsViews),
      );

      parentDirectoryService.onValueChange(record, view.fieldId, serializedRecords);
    };

    return debounce(_fn, 16);
  }, [
    parentDirectoryService,
    record,
    tableControl.controlsViewsMap,
    tableControl.recordControlsViews,
    tableControl.records,
    view.fieldId,
  ]);

  useEffect(() => {
    const updateDataDisposer = tableControl.eventEmitter.addEventListener(
      'recordDataUpdated',
      syncMainTableWithSubtable,
    );

    return () => {
      updateDataDisposer();
    };
  }, [syncMainTableWithSubtable, tableControl]);

  const syncSubtableWithMainTableData = useMemo(() => {
    const _fn = (record: DirectoryRecord) => {
      const value = record.data[view.fieldId];

      if (!Array.isArray(value)) {
        return;
      }

      value.forEach((v) => {
        if (!isObjectWithKeys(v)) {
          console.error('wrong value type', v);
          return;
        }
        const subtableRecordIdKey = view.rowIdKey ?? 'id';
        const id = v[subtableRecordIdKey];

        if (typeof id !== 'number' && typeof id !== 'string') {
          console.error('wrong record id type');
          return;
        }

        const subtableRecord = tableControl.recordsMap.get(id);

        if (!subtableRecord) {
          console.error('cannot find corresponding record');
          return;
        }

        tableControl.recordControlsViews.forEach((view) => {
          if (!view.attrName) {
            return;
          }

          tableControl.onValueChange(subtableRecord, view.fieldId, v[view.fieldId]);
        });
      });
    };

    return debounce(_fn, 16);
  }, [tableControl, view]);

  useEffect(() => {
    const mainDirectoryChangedControlValueDisposer = parentDirectoryService.eventEmitter.addEventListener(
      'recordDataUpdated',
      ({ record, fieldId }) => {
        if (fieldId === view.fieldId) {
          syncSubtableWithMainTableData(record);
        }
      },
    );

    return () => {
      mainDirectoryChangedControlValueDisposer();
    };
  }, [parentDirectoryService.eventEmitter, syncSubtableWithMainTableData, tableControl, view]);

  useEffect(() => {
    syncSubtableWithMainTableData(record);
  }, [record, syncSubtableWithMainTableData]);

  return (
    <>
      <div className={clsx(styles.tableWrapper, styles.wrapper)}>
        <Table
          initialState={{
            filter: {
              filterModel: displayManager.settings.filterModel,
            },
            sorting: {
              sortModel: displayManager.settings.sortingModel,
            },
            pinnedColumns: displayManager.settings.pinnedColumns,
          }}
          apiRef={tableApi}
          columns={columns}
          rows={tableControl.records}
          sx={tableSx}
          language={muiLanguageKeys[localization.language]}
          valueDragging
          onValueDraggingEnd={tableControl.directoryViewMediator?.handleValueDraggingEnd}
          serviceColumns={SERVICE_COLUMNS}
          columnsOrderModel={displayManager.columnsOrderModel}
          columnVisibilityModel={displayManager.settings.columnsVisibility ?? {}}
          pinnedColumns={displayManager.settings.pinnedColumns}
          onCellEditStop={handleComboboxEnterPress}
          onCellEditStart={handleCellDeletePress}
          onFilterModelChange={(model) => displayManager.onFilterChange(model)}
          onColumnVisibilityModelChange={displayManager.onColumnVisibilityChange}
          onPinnedColumnsChange={displayManager.onPinChange}
          onSortModelChange={(model) => displayManager.onSortChange(convertToTableSortItems(model))}
          onColumnResize={handleWidthChange}
          isCellEditable={(params) => {
            const row = tableControl.recordsMap.get(params.id as number);
            const fieldRulesData = row?.fieldRulesData[params.field];

            if (!fieldRulesData) {
              return !!params.colDef.editable;
            }

            return !(fieldRulesData?.isDisabled || fieldRulesData?.isEnteringBlocked);
          }}
          onColumnsOrderModelChange={displayManager.onColumnsOrderModelChange}
          slotProps={slotProps}
          className={styles.table}
          checkboxSelection
          disableAggregation
          disableRowGrouping
          unstable_cellSelection
          columnGroupingModel={groupingModel}
          getRowId={(row) => row.recordId}
          onRowSelectionModelChange={(model) => {
            tableControl.directoryViewMediator?.setIsCopyAvailable(!!model.length);
            tableControl.directoryViewMediator?.updateSelectedRows(model);
          }}
          onClipboardCopy={(_, event) => {
            event.defaultMuiPrevented = true;
          }}
          onCellKeyDown={(_, event) => {
            if (event.code === 'KeyV' && (event.metaKey || event.ctrlKey)) {
              event.defaultMuiPrevented = true;
            }
          }}
          getCellClassName={getCellClassName}
        />
      </div>
      {tableApi.current && (
        <Drawer
          className={styles.displaySettingsWrapper}
          style={{ width: 320 }}
          open={displayManager.isSettingsOpen}
          onClose={() => displayManager.setSettingsOpen(false)}
          title={<p className={styles.title}>{t('displaySettings:title')}</p>}
        >
          <DisplaySettings
            fieldViews={tableControl.recordControlsViews}
            columns={columns}
            displayManager={displayManager}
            minVisibleColumns={MIN_VISIBLE_COLUMNS}
          />
        </Drawer>
      )}
    </>
  );
});
